/**
 * Normalize render_segments text ranges so each character of assistant content
 * is painted at most once.
 *
 * Bug this fixes: tool/reasoning insertion often left multiple `text-after-*`
 * segments with `startOffset` set but `endOffset` unset. The renderer treated
 * missing endOffset as "to end of content", so after every tool card the UI
 * re-printed the entire remaining answer (the $21.21 "repetition" report).
 * Stored `messages.content` was clean — only the segmented paint was wrong.
 */

export type RenderSegmentLike =
  | {
      id: string
      type: 'text'
      sequence: number
      startOffset: number
      endOffset?: number
    }
  | {
      id: string
      type: 'tool'
      sequence: number
      toolId: string
      textOffset: number
    }
  | {
      id: string
      type: 'reasoning'
      sequence: number
      textOffset: number
      text: string
      durationMs?: number
    }

function segmentPosition(segment: RenderSegmentLike): number {
  if (segment.type === 'text') return segment.startOffset
  return segment.textOffset
}

export function sortRenderSegments<T extends RenderSegmentLike>(segments: T[]): T[] {
  return segments.slice().sort((a, b) => {
    const posA = segmentPosition(a)
    const posB = segmentPosition(b)
    if (posA !== posB) return posA - posB
    // Same offset: tools/reasoning must paint before text. Otherwise an
    // open text-0 at offset 0 steals the whole answer and "Worked for"
    // sticks under the prose forever (tools-before-first-token path).
    const rank = (segment: RenderSegmentLike) =>
      segment.type === 'tool' || segment.type === 'reasoning' ? 0 : 1
    const rankDiff = rank(a) - rank(b)
    if (rankDiff !== 0) return rankDiff
    return a.sequence - b.sequence
  })
}

/**
 * Resolve an exclusive [start, end) for a text segment given the sorted list.
 * Advances `textCursor` so later segments cannot re-paint earlier bytes.
 */
export function resolveExclusiveTextRange(
  segment: Extract<RenderSegmentLike, { type: 'text' }>,
  index: number,
  sorted: RenderSegmentLike[],
  contentLength: number,
  textCursor: number
): { start: number; end: number; nextCursor: number } {
  const startOffset = Math.max(0, segment.startOffset || 0)
  let boundary =
    typeof segment.endOffset === 'number' ? Math.max(0, segment.endOffset) : contentLength

  for (let j = index + 1; j < sorted.length; j += 1) {
    const pos = segmentPosition(sorted[j])
    if (Number.isFinite(pos) && pos > startOffset) {
      boundary = Math.min(boundary, pos)
      break
    }
  }

  const start = Math.max(startOffset, textCursor)
  const end = Math.max(start, Math.min(boundary, contentLength))
  return { start, end, nextCursor: end }
}

/**
 * Close every open text segment that ends at or before `offset`.
 * Used when inserting a tool/reasoning boundary during streaming.
 */
export function closeOpenTextSegmentsAt(
  segments: Array<Record<string, unknown>>,
  offset: number
): void {
  for (const seg of segments) {
    if (!seg || seg.type !== 'text') continue
    if (typeof seg.startOffset !== 'number') continue
    if (seg.endOffset != null) continue
    if (seg.startOffset <= offset) {
      seg.endOffset = offset
    }
  }
}
